import Image, { type ImageProps } from "next/image";
import { cn } from "@drpcrd/utils";
import { Typography } from "@drpcrd/shared/ui";

type Props = {
  quote: string;
  /** Classes applied to the bubble — include both bg and text color (e.g. "bg-primary-50 text-primary-600"). */
  bubbleClassName: string;
  avatarSrc: ImageProps["src"];
  name: string;
  subtitle: string;
  /** Padding for the quote bubble. Defaults to "p-3 sm:p-4". */
  bubblePadding?: string;
  /** Classes for the attribution row. Defaults to "mt-3 flex items-center gap-2". */
  attributionClassName?: string;
  /** Border-radius class for the avatar. Defaults to "rounded-full". */
  avatarClassName?: string;
};

/**
 * Reusable testimonial quote bubble with rounded speech-bubble corners and avatar attribution.
 */
export const TestimonialQuote = ({
  quote,
  bubbleClassName,
  avatarSrc,
  name,
  subtitle,
  bubblePadding = "p-3 sm:p-4",
  attributionClassName = "mt-3 flex items-center gap-2",
  avatarClassName = "rounded-full",
}: Props) => (
  <div>
    <div
      className={cn(
        "rounded-tl-3xl rounded-tr-3xl rounded-br-3xl",
        bubblePadding,
        bubbleClassName,
      )}
    >
      <Typography
        as="h3"
        color="inherit"
        className="font-poppins text-base leading-[125%] font-semibold tracking-[-0.64px] sm:text-2xl sm:tracking-[-0.96px] lg:text-4xl lg:tracking-[-1.44px]"
      >
        &lsquo;{quote}&rsquo;
      </Typography>
    </div>
    <div className={attributionClassName}>
      <Image
        src={avatarSrc}
        alt={name}
        width={64}
        height={64}
        className={cn(
          "size-6 shrink-0 object-cover lg:size-8",
          avatarClassName,
        )}
      />
      <div>
        <Typography size="lg" weight="semibold" color="black">
          {name}
        </Typography>
        <Typography size="sm" color="neutral">
          {subtitle}
        </Typography>
      </div>
    </div>
  </div>
);
